/**
 * Iter59 — ContactosTab extraído de ClienteDetalle.jsx.
 *
 * Lista de contactos en cards + organigrama jerárquico. Sin estado propio:
 * recibe `contactos` y `organigrama` ya construido + handlers del padre.
 */
import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Mail, Phone, History, UserCog, UserPlus, Trash2,
} from 'lucide-react';
import { Tree } from 'react-organizational-chart';
import { NivelDot, OrgCard, OrgBranch } from './_shared';

export const ContactosTab = ({ contactos, organigrama, onNuevo, onEditar, onBorrar, onVerCorreos, entidadId }) => (
  <div data-testid="tab-contactos-content">
    <div className="flex items-center justify-between mb-4">
      <p className="text-sm text-ink-500">Personas empleadas actualmente en este cliente. El organigrama se construye con "jefe directo".</p>
      <Button className="bg-brand-500 hover:bg-brand-600 text-white" onClick={onNuevo} data-testid="btn-nuevo-contacto">
        <UserPlus className="w-4 h-4 mr-1.5" /> Añadir contacto
      </Button>
    </div>

    {contactos.length === 0 ? (
      <Card className="p-10 text-center text-ink-400 bg-white border-ink-200">
        No hay contactos aún. Añade el primero.
      </Card>
    ) : (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {contactos.map((c) => (
            <Card key={c.id} className="p-4 border-ink-200 bg-white card-hover" data-testid={`contacto-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className="relative">
                  <div className="h-10 w-10 rounded-full bg-ink-100 text-ink-600 flex items-center justify-center font-bold text-sm">
                    {c.nombre[0]?.toUpperCase()}
                  </div>
                  <NivelDot nivel={c.nivel_relacion} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-ink-900 truncate">{c.nombre} {c.apellidos || ''}</p>
                    {c.es_decisor && <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] px-1.5 py-0">Decisor</Badge>}
                  </div>
                  {c.cargo && <p className="text-xs text-ink-500 truncate">{c.cargo}{c.departamento && ` · ${c.departamento}`}</p>}
                  {c.division && (
                    <p className="text-[11px] text-brand-700 bg-brand-50 inline-block px-1.5 py-0.5 rounded mt-1 border border-brand-100" data-testid={`contacto-div-${c.id}`}>
                      Div. {c.division}
                    </p>
                  )}
                  {/* Badge delegación — visible cuando el contacto pertenece a una delegación hija */}
                  {entidadId && c.entidad_id && c.entidad_id !== entidadId && c.entidad_nombre && (
                    <p className="text-[11px] text-violet-700 bg-violet-50 inline-block px-1.5 py-0.5 rounded mt-1 border border-violet-100" data-testid={`contacto-deleg-${c.id}`}>
                      📍 {c.entidad_nombre}
                    </p>
                  )}
                  {c.email_trabajo && <p className="text-xs text-ink-600 mt-1 flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {c.email_trabajo}</p>}
                  {(c.telefono_trabajo || c.movil_trabajo) && (
                    <p className="text-xs text-ink-600 flex items-center gap-1 truncate"><Phone className="w-3 h-3" /> {c.movil_trabajo || c.telefono_trabajo}</p>
                  )}
                  {c.historico_empleos?.length > 0 && (
                    <p className="text-[11px] text-ink-400 mt-1.5 flex items-center gap-1">
                      <History className="w-3 h-3" /> {c.historico_empleos.length} empleo{c.historico_empleos.length > 1 ? 's' : ''} previo{c.historico_empleos.length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => onEditar(c)} className="p-1.5 rounded hover:bg-ink-100 text-ink-600" title="Editar" data-testid={`btn-edit-contacto-${c.id}`}>
                    <UserCog className="w-4 h-4" />
                  </button>
                  {onVerCorreos && c.email_trabajo && (
                    <button onClick={() => onVerCorreos(c)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Ver correos de este contacto" data-testid={`btn-correos-contacto-${c.id}`}>
                      <Mail className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => onBorrar(c)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Card className="p-6 border-ink-200 bg-white" data-testid="organigrama-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-ink-800">Organigrama</h3>
            <span className="text-xs text-ink-400">
              {contactos.length} {contactos.length === 1 ? 'persona' : 'personas'}
              {organigrama.length > 0 && ` · ${organigrama.length} raíz${organigrama.length === 1 ? '' : 'es'}`}
            </span>
          </div>
          {organigrama.length === 0 ? (
            <p className="text-sm text-ink-400">
              Sin jerarquía definida todavía. Asigna un "Jefe directo" al editar un contacto y aparecerá el árbol.
            </p>
          ) : (
            <div className="overflow-x-auto pb-3" data-testid="organigrama-tree">
              {organigrama.length === 1 ? (
                <Tree
                  lineWidth="2px"
                  lineColor="#cbd5e1"
                  lineBorderRadius="6px"
                  label={<OrgCard contacto={organigrama[0]} onClick={() => onEditar(organigrama[0])} />}
                >
                  {organigrama[0].children.map((child) => (
                    <OrgBranch key={child.id} node={child} onClick={onEditar} />
                  ))}
                </Tree>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {organigrama.map((root) => (
                    <Tree
                      key={root.id}
                      lineWidth="2px"
                      lineColor="#cbd5e1"
                      lineBorderRadius="6px"
                      label={<OrgCard contacto={root} onClick={() => onEditar(root)} />}
                    >
                      {root.children.map((child) => (
                        <OrgBranch key={child.id} node={child} onClick={onEditar} />
                      ))}
                    </Tree>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      </>
    )}
  </div>
);

export default ContactosTab;
